
import { Component, Mixins } from 'vue-property-decorator';
import Page from '@/views/Page.vue';
import { PageState } from '@/models/PageState';
import { VuetifyMixin, PaginatedTableMixin, StringsMixin, BAIconsMixin, CoachRoutingMixin } from '@/mixins';
import { ScoutingReportModel, ScoutingReportModelEx } from '@/models/scoutingReport/ScoutingReportModel';
import { adminStore, notificationStore } from '@/store';
import { DataTableHeader } from 'vuetify';
import { AthleteProfileModel } from '@/models';
import ScoutingReportBrief from '@/components/profile/athlete/ScoutingReportBrief.vue';
import { PartialAthleteProfile, PartialCoachProfile, PartialTeam } from '@/models';
import { PartialOrganization } from '@/models/organization/OrganizationModel';
import { SportModel } from '@/models/sport';
import { ObjectCache } from '@/helpers';

@Component({
	components: {
		Page,
		ScoutingReportBrief
	}
})
export default class ScoutingReportsAdminDashboard extends Mixins(VuetifyMixin, StringsMixin, PaginatedTableMixin, BAIconsMixin, CoachRoutingMixin){
	pageState: PageState = new PageState('Initial');

	mounted(): void{
		this.tableOptions.sortBy = ['createdAt'];
		this.tableOptions.sortDesc = [true];
		this.localForagePersistFields = [['search', ''],['tableOptions.page', 1],['tableOptions.itemsPerPage', 25]];
	}

	isShowComplete: boolean = true;
	isShowIncomplete: boolean = true;
	onShowComplete() {
		this.isShowComplete = !this.isShowComplete;
		this.updateFilter();
	}
	onShowIncomplete() {
		this.isShowIncomplete = !this.isShowIncomplete;
		this.updateFilter();
	}

	athletesCache: ObjectCache<PartialAthleteProfile> = new ObjectCache<PartialAthleteProfile>();
	coachesCache: ObjectCache<PartialCoachProfile> = new ObjectCache<PartialCoachProfile>();
	teamsCache: ObjectCache<PartialTeam> = new ObjectCache<PartialTeam>();
	orgsCache: ObjectCache<PartialOrganization> = new ObjectCache<PartialOrganization>();
	sportsCache: ObjectCache<SportModel> = new ObjectCache<SportModel>();
	async loadDetails(): Promise<Array<ScoutingReportModelEx>> {
		return Promise.all(adminStore.scoutingReports.map(async(report) => {
			const ex = new ScoutingReportModelEx();
			await ex.fromReport(report, this.athletesCache, this.coachesCache, this.teamsCache, this.orgsCache, this.sportsCache);
			return ex;
		}));
	}
	async loadTable(): Promise<void> {
		this.isLoaded = false;
		this.isLoading = true;
		try {
			const query = this.TableQuery<ScoutingReportModel>(this.search, ['athleteId', 'notes', 'playerDetails.lastName', 'playerDetails.firstName', 'playerDetails.email', 'scoutingReport.gameDetails.teamName']);
			if( !this.isShowComplete && !this.isShowIncomplete ) query.$match = {...query.$match, ...{ 'id': 'none'}};
			else if( !this.isShowComplete ) query.$match = {...query.$match, ...{ 'submitted': false}};
			else if( !this.isShowIncomplete ) query.$match = {...query.$match, ...{ 'submitted': true}};
			await adminStore.loadScoutingReports({ query, options: this.TableQueryOptions });
			this.scoutingReports = await this.loadDetails();
			this.dataItemsCount = adminStore.scoutingReportsCount;
		} catch(e) {
			notificationStore.pushSnackbarError({message: `Error loading scouting reports: ${e}`});
		} finally {
			this.pageState = new PageState('Ready');
		}
		this.isLoaded = true;
		this.isLoading = false;
	}

	get TableLoading(): boolean {
		return this.isLoading;
	}
	get PageLoading(): boolean {
		return adminStore.scoutingReportsLoading || this.isLoading;
	}

	get TableHeaders(): DataTableHeader<any>[] {
		return [
			{ text: 'Date', value: 'createdAt', sortable: true },
			{ text: 'ID', value: 'id', sortable: false },
			{ text: 'Player', value: 'name', sortable: false },
			{ text: 'Coach', value: 'coach', sortable: false },
			{ text: 'Status', value: 'submitted', sortable: false },
			{ text: 'Score', value: 'performance', sortable: false, width:'20', align:'center' },
			{ text: 'AthleteId', value: 'athleteId', sortable: false },
			{ text: '', value: 'actions', sortable: false },
			{ text: '', value: 'data-table-expand', sortable: false },
		]
	}

	scoutingReports: Array<ScoutingReportModelEx> = [];
	get ScoutingReports(): Array<ScoutingReportModel> {
		if( !adminStore.scoutingReportsInitialized) return [];
		return this.scoutingReports;
	}
	get ScoutingReportsCount(): number {
		return adminStore.scoutingReportsCount;
	}

	editingReport: boolean = false;
	editReport: ScoutingReportModel | null = null;
	editLinkAthlete: AthleteProfileModel | null = null;
	onLinkScoutingReport(report: ScoutingReportModel): void {
		this.editReport = report;
		this.editingReport = true;
	}
	onUpdateEditAthlete(athlete: AthleteProfileModel): void {
		this.editLinkAthlete = athlete?? null;
	}
	onCancelEdit(): void {
		this.editReport = null;
		this.editingReport = false;
	}
	onRemoveLink(): void {
		try {
			this.editReport.athleteId = null;
			this.editReport.lastModified = new Date();
			this.updateScoutingReport(this.editReport);
		} catch(e) {
			notificationStore.pushSnackbarError(e);
		}
	}
	onConfirmEdit(): void {
		if( !this.editLinkAthlete ) return;

		try {
			this.editReport.athleteId = this.editLinkAthlete.id;
			this.editReport.lastModified = new Date();
			this.editReport.playerDetails.firstName = this.editLinkAthlete.firstName;
			this.editReport.playerDetails.lastName = this.editLinkAthlete.lastName;
			if( this.editLinkAthlete.birthDate ) {
				this.editReport.playerDetails.dateOfBirth = this.editLinkAthlete.birthDate;
				this.editReport.playerDetails.yearOfBirth = this.editLinkAthlete.birthDate.getFullYear();
			}
			this.updateScoutingReport(this.editReport);
		} catch(e) {
			notificationStore.pushSnackbarError(e);
		}
	}
	async updateScoutingReport(scoutingReport: ScoutingReportModel) {
		try {
			const updatedReport = await adminStore.patchScoutingReport(scoutingReport);
			if( this.editReport.athleteId ) {
				notificationStore.pushSnackbarSuccess({message: "Report " + scoutingReport.id + " linked to " + this.editReport.PlayerName})
			} else {
				notificationStore.pushSnackbarWarning({message: "Report " + scoutingReport.id + " UNLINKED FROM " + this.editReport.PlayerName})
			}
			this.editReport = null;
			this.editingReport = false;
			this.editLinkAthlete = null;
			this.updateSearch();
		} catch(e) {
			notificationStore.pushSnackbarError(e);
		}
	}


	viewByCoach: boolean = false;
}
